import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MdMonitorHeart, MdAccountBalanceWallet, MdFavorite,
  MdThermostat, MdAir, MdWater, MdTimer, MdGridView,
  MdTableRows, MdTrendingUp, MdWarning, MdAdd,
  MdLocalDrink, MdOutput,
} from 'react-icons/md';
import { PageShell, Badge, DataTable, StatCard } from '../../components/Ui';
import { useAuth } from '../../auth/AuthContext';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { useHmsRecords, useHmsConfig } from '../../hooks/useHmsRecords';

/* ── Vitals mini-gauge ── */
function VitalGauge({ label, value, unit, icon: Icon, color, low, high }) {
  const numVal = parseFloat(value);
  const isAbnormal = value && !Number.isNaN(numVal) && (
    (low != null && numVal < low) || (high != null && numVal > high)
  );
  return (
    <div className="vg-card" style={{ borderColor: isAbnormal ? '#fca5a5' : '#e2e8f0', background: isAbnormal ? '#fff5f5' : '#fff' }}>
      <div className="vg-head" style={{ color }}>
        <Icon style={{ fontSize: 18 }} />
        <span className="vg-label">{label}</span>
        {isAbnormal && <MdWarning style={{ fontSize: 14, color: '#ef4444', marginLeft: 'auto' }} />}
      </div>
      <div className="vg-val" style={{ color: isAbnormal ? '#ef4444' : '#0f172a' }}>{value || '—'}</div>
      {unit && <div className="vg-unit">{unit}</div>}
      {low != null && high != null && <div className="vg-range">Normal: {low}–{high}</div>}
    </div>
  );
}

function VitalsPanel({ adm }) {
  const v = adm.vitals || {};
  return (
    <div className="vitals-panel">
      <div className="vitals-grid">
        <VitalGauge label="Blood Pressure" value={v.bp} unit="mmHg" icon={MdFavorite} color="#ef4444" />
        <VitalGauge label="Pulse" value={v.pulse} unit="bpm" icon={MdTrendingUp} color="#f97316" low={60} high={100} />
        <VitalGauge label="Temperature" value={v.temp} unit="°F" icon={MdThermostat} color="#eab308" low={97} high={99.5} />
        <VitalGauge label="SpO₂" value={v.spo2} unit="%" icon={MdAir} color="#06b6d4" low={95} high={100} />
        <VitalGauge label="Resp. Rate" value={v.rr} unit="/min" icon={MdTimer} color="#8b5cf6" low={12} high={20} />
        <VitalGauge label="Weight" value={v.weight} unit="kg" icon={MdWater} color="#10b981" />
      </div>
      {v.notes && <div className="vitals-notes"><strong>Nursing notes:</strong> {v.notes}</div>}
    </div>
  );
}

/* ── Medication Administration Record (MAR) ── */
function MarTab({ admissions }) {
  const TODAY = new Date().toLocaleDateString('en-IN');
  const [records, setRecords] = useState({});
  const [busy, setBusy] = useState('');
  const times = ['06:00', '08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00', '22:00', '00:00'];

  const markGiven = async (admId, medName, time) => {
    const key = `${admId}_${medName}_${time}`;
    const nextGiven = records[key] === 'given' ? '' : 'given';
    setRecords((r) => ({ ...r, [key]: nextGiven }));
    if (!nextGiven) return;
    setBusy(key);
    try {
      await ipApi.addAdmissionActivity(admId, {
        kind: 'mar',
        title: `MAR: ${medName} @ ${time}`,
        note: `Medication ${medName} marked given at ${time}`,
        scheduledTime: time,
      });
    } catch (e) {
      setRecords((r) => ({ ...r, [key]: '' }));
      window.alert(e.message || 'Could not save MAR — try again');
    } finally {
      setBusy('');
    }
  };

  return (
    <div>
      <div className="mar-header">
        <span className="mar-date">📅 MAR for: {TODAY}</span>
        <span className="hint">Click a time cell to mark medication as given (✓) or clear</span>
      </div>
      {admissions.length === 0 && <p className="hint table-empty">No inpatients to show MAR for.</p>}
      {admissions.map((adm) => {
        const meds = adm.activeMeds || adm.medications || [];
        if (!meds.length) {
          return (
            <div key={adm.admissionId} className="mar-patient-block">
              <div className="mar-patient-name">{adm.patientName} <span className="hint">· {adm.ward} · Bed {adm.bedLabel || adm.bedId}</span></div>
              <div className="hint" style={{ padding: '8px 0', color: '#94a3b8' }}>No active medications — add from IPD chart</div>
            </div>
          );
        }
        return (
          <div key={adm.admissionId} className="mar-patient-block">
            <div className="mar-patient-name">{adm.patientName} <span className="hint">· {adm.ward} · Bed {adm.bedLabel || adm.bedId} · Dr. {adm.doctor}</span></div>
            <div className="mar-table-wrap">
              <table className="mar-table">
                <thead>
                  <tr>
                    <th>Medicine</th>
                    <th>Dose / Route</th>
                    {times.map((t) => <th key={t}>{t}</th>)}
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {meds.map((m, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{m.name || `Medicine ${i + 1}`}</td>
                      <td style={{ fontSize: 11, color: '#64748b' }}>{m.dose || '—'} {m.route || ''}</td>
                      {times.map((t) => {
                        const key = `${adm.admissionId}_${m.name}_${t}`;
                        const given = records[key] === 'given';
                        return (
                          <td key={t}>
                            <button
                              type="button"
                              className="mar-cell-btn"
                              style={{
                                background: given ? '#16a34a' : '#f1f5f9',
                                color: given ? '#fff' : '#94a3b8',
                              }}
                              disabled={busy === key}
                              onClick={() => markGiven(adm.admissionId, m.name, t)}
                              title={given ? 'Mark as not given' : 'Mark as given'}
                            >
                              {given ? '✓' : '·'}
                            </button>
                          </td>
                        );
                      })}
                      <td><input className="input input-sm" style={{ width: 120 }} placeholder="Notes" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Nurse Task List / Duty Checklist ── */
const DEFAULT_TASKS = [
  'Morning vitals check — all patients',
  'Medication round — 8 AM',
  'Dressing changes — ward A',
  'Blood sample collection — pending orders',
  'IV line check and flush',
  'Patient hygiene care',
  'Noon vitals check',
  'Medication round — 2 PM',
  'Doctor round assistance',
  'Evening vitals check',
  'Night medication round',
  'Handover to night duty nurse',
];

function TaskListTab() {
  const { config, loading, error, save } = useHmsConfig('nurse-duty-tasks');
  const [tasks, setTasks] = useState([]);
  const [hydrated, setHydrated] = useState(false);
  const [newTask, setNewTask] = useState('');
  const [newPriority, setNewPriority] = useState('normal');
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  React.useEffect(() => {
    if (hydrated || loading) return;
    const stored = config?.tasks;
    if (Array.isArray(stored) && stored.length) {
      setTasks(stored);
    } else {
      setTasks(DEFAULT_TASKS.map((t, i) => ({ id: i, text: t, done: false, priority: 'normal', time: '' })));
    }
    setHydrated(true);
  }, [config, loading, hydrated]);

  const persistTasks = async (nextTasks) => {
    setSaveBusy(true);
    setSaveErr('');
    try {
      await save({ tasks: nextTasks });
    } catch (e) {
      setSaveErr(e.message || 'Failed to save tasks');
    } finally {
      setSaveBusy(false);
    }
  };

  const addTask = () => {
    if (!newTask.trim()) return;
    const next = [...tasks, { id: Date.now(), text: newTask, done: false, priority: newPriority, time: '' }];
    setTasks(next);
    setNewTask('');
    persistTasks(next);
  };

  const toggle = (id) => {
    const next = tasks.map((x) => (x.id === id ? { ...x, done: !x.done } : x));
    setTasks(next);
    persistTasks(next);
  };
  const remove = (id) => {
    const next = tasks.filter((x) => x.id !== id);
    setTasks(next);
    persistTasks(next);
  };
  const setTime = (id, time) => {
    const next = tasks.map((x) => (x.id === id ? { ...x, time } : x));
    setTasks(next);
    persistTasks(next);
  };

  const done = tasks.filter((t) => t.done).length;
  const urgent = tasks.filter((t) => t.priority === 'urgent' && !t.done).length;

  return (
    <div>
      {loading && !hydrated && <p className="hint mb">Loading duty tasks…</p>}
      {error && <div className="error mb">{error}</div>}
      {saveErr && <div className="error mb">{saveErr}</div>}
      {saveBusy && <p className="hint mb">Saving tasks…</p>}
      <div className="tasklist-header">
        <span className="tasklist-stat" style={{ color: '#10b981' }}>✓ Completed: {done}/{tasks.length}</span>
        {urgent > 0 && <span className="tasklist-stat" style={{ color: '#ef4444' }}>🔴 Urgent: {urgent}</span>}
      </div>
      <div className="tasklist-add-row">
        <input className="input" placeholder="Add a task…" value={newTask} onChange={(e) => setNewTask(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addTask()} style={{ flex: 1 }} />
        <select className="input input-sm" value={newPriority} onChange={(e) => setNewPriority(e.target.value)} style={{ width: 110 }}>
          <option value="normal">Normal</option>
          <option value="urgent">🔴 Urgent</option>
          <option value="low">Low</option>
        </select>
        <button type="button" className="btn btn-primary" onClick={addTask}><MdAdd /> Add</button>
      </div>
      <div className="tasklist-items">
        {tasks.map((task) => (
          <div key={task.id} className="tasklist-item" style={{ opacity: task.done ? 0.5 : 1 }}>
            <button type="button" className="tasklist-check" style={{ background: task.done ? '#10b981' : '#f1f5f9', color: task.done ? '#fff' : '#94a3b8' }} onClick={() => toggle(task.id)}>
              {task.done ? '✓' : '○'}
            </button>
            <span className="tasklist-text" style={{ textDecoration: task.done ? 'line-through' : 'none' }}>
              {task.priority === 'urgent' && !task.done && <span style={{ color: '#ef4444', marginRight: 5 }}>🔴</span>}
              {task.text}
            </span>
            <input type="time" className="input input-sm" style={{ width: 90, marginLeft: 'auto' }} value={task.time} onChange={(e) => setTime(task.id, e.target.value)} />
            <button type="button" className="btn btn-sm btn-secondary" onClick={() => remove(task.id)} style={{ padding: '4px 8px' }}>×</button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Intake / Output Chart ── */
function IOChartTab({ admissions }) {
  const [ioData, setIoData] = useState({});
  const [selectedAdm, setSelectedAdm] = useState(admissions[0]?.admissionId || '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const adm = admissions.find((a) => a.admissionId === selectedAdm);

  const IO_TYPES = {
    intake: ['Oral fluids', 'IV fluids', 'NGT feeds', 'Blood/products'],
    output: ['Urine', 'Vomiting', 'Drainage', 'Stool', 'Nasogastric'],
  };

  const updateIO = (admId, category, type, val) => {
    setIoData((d) => ({ ...d, [`${admId}_${category}_${type}`]: val }));
  };

  const totalIntake = IO_TYPES.intake.reduce((s, t) => s + (Number(ioData[`${selectedAdm}_intake_${t}`]) || 0), 0);
  const totalOutput = IO_TYPES.output.reduce((s, t) => s + (Number(ioData[`${selectedAdm}_output_${t}`]) || 0), 0);
  const balance = totalIntake - totalOutput;

  const saveIO = async () => {
    if (!selectedAdm) return;
    setBusy(true);
    setMsg('');
    try {
      await ipApi.addAdmissionActivity(selectedAdm, {
        kind: 'fluid_balance',
        title: `I/O chart — intake ${totalIntake} mL, output ${totalOutput} mL`,
        data: {
          date: new Date().toISOString().slice(0, 10),
          intake: totalIntake,
          output: totalOutput,
          balance,
          details: ioData,
        },
      });
      setMsg('I/O chart saved to patient record.');
    } catch (e) {
      setMsg(e.message || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {admissions.length > 0 && (
        <div className="row-actions mb">
          <label style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Patient:</label>
          <select className="input" style={{ maxWidth: 300 }} value={selectedAdm} onChange={(e) => setSelectedAdm(e.target.value)}>
            {admissions.map((a) => (
              <option key={a.admissionId} value={a.admissionId}>
                {a.patientName} — {a.ward} Bed {a.bedLabel || a.bedId}
              </option>
            ))}
          </select>
        </div>
      )}

      {adm && (
        <>
          <div className="io-balance-row">
            <div className="io-balance-card" style={{ borderColor: '#86efac', background: '#f0fdf4' }}>
              <MdLocalDrink style={{ color: '#16a34a', fontSize: 22 }} />
              <div>
                <div className="io-balance-val" style={{ color: '#16a34a' }}>{totalIntake} mL</div>
                <div className="io-balance-label">Total Intake</div>
              </div>
            </div>
            <div className="io-balance-card" style={{ borderColor: '#fca5a5', background: '#fef2f2' }}>
              <MdOutput style={{ color: '#dc2626', fontSize: 22 }} />
              <div>
                <div className="io-balance-val" style={{ color: '#dc2626' }}>{totalOutput} mL</div>
                <div className="io-balance-label">Total Output</div>
              </div>
            </div>
            <div className="io-balance-card" style={{ borderColor: balance >= 0 ? '#93c5fd' : '#fca5a5', background: balance >= 0 ? '#eff6ff' : '#fef2f2' }}>
              <div>
                <div className="io-balance-val" style={{ color: balance >= 0 ? '#2563eb' : '#dc2626' }}>
                  {balance >= 0 ? '+' : ''}{balance} mL
                </div>
                <div className="io-balance-label">Fluid Balance</div>
              </div>
            </div>
          </div>

          <div className="io-grid">
            <div className="io-section">
              <div className="io-section-title" style={{ color: '#16a34a' }}>⬆ INTAKE (mL)</div>
              {IO_TYPES.intake.map((t) => (
                <div key={t} className="io-row">
                  <label>{t}</label>
                  <input
                    type="number"
                    className="input input-sm"
                    style={{ width: 100 }}
                    placeholder="0"
                    value={ioData[`${selectedAdm}_intake_${t}`] || ''}
                    onChange={(e) => updateIO(selectedAdm, 'intake', t, e.target.value)}
                  />
                  <span className="hint">mL</span>
                </div>
              ))}
            </div>
            <div className="io-section">
              <div className="io-section-title" style={{ color: '#dc2626' }}>⬇ OUTPUT (mL)</div>
              {IO_TYPES.output.map((t) => (
                <div key={t} className="io-row">
                  <label>{t}</label>
                  <input
                    type="number"
                    className="input input-sm"
                    style={{ width: 100 }}
                    placeholder="0"
                    value={ioData[`${selectedAdm}_output_${t}`] || ''}
                    onChange={(e) => updateIO(selectedAdm, 'output', t, e.target.value)}
                  />
                  <span className="hint">mL</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={saveIO}>{busy ? 'Saving…' : 'Save I/O to patient chart'}</button>
            {msg && <span className="hint" style={{ color: msg.includes('saved') ? '#10b981' : '#dc2626' }}>{msg}</span>}
          </div>
        </>
      )}
      {admissions.length === 0 && <p className="hint table-empty">No inpatients to chart.</p>}
    </div>
  );
}

/* ── Fall Risk Assessment ── */
const FALL_QUESTIONS = [
  { q: 'History of falls in past 3 months?', opt: ['No (0)', 'Yes (25)'], score: [0, 25] },
  { q: 'Secondary diagnosis?', opt: ['No (0)', 'Yes (15)'], score: [0, 15] },
  { q: 'Ambulatory aids?', opt: ['None / Bed rest / Nurse assist (0)', 'Crutches / Cane / Walker (15)', 'Furniture (30)'], score: [0, 15, 30] },
  { q: 'IV / Heparin Lock?', opt: ['No (0)', 'Yes (20)'], score: [0, 20] },
  { q: 'Gait / Transferring?', opt: ['Normal / Bed rest / Immobile (0)', 'Weak (10)', 'Impaired (20)'], score: [0, 10, 20] },
  { q: 'Mental status?', opt: ['Oriented to own ability (0)', 'Forgets limitations (15)'], score: [0, 15] },
];

function FallRiskTab({ admissions }) {
  const [selectedAdm, setSelectedAdm] = useState(admissions[0]?.admissionId || '');
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const totalScore = FALL_QUESTIONS.reduce((s, q, i) => {
    const ans = answers[`q${i}`];
    return s + (ans != null ? q.score[ans] || 0 : 0);
  }, 0);

  const risk = totalScore < 25 ? { label: 'Low Risk', color: '#16a34a', bg: '#f0fdf4' }
    : totalScore < 45 ? { label: 'Medium Risk', color: '#ca8a04', bg: '#fefce8' }
    : { label: 'HIGH RISK', color: '#dc2626', bg: '#fef2f2' };

  const saveAssessment = async () => {
    if (!selectedAdm) return;
    setBusy(true);
    setMsg('');
    try {
      await ipApi.addAdmissionActivity(selectedAdm, {
        kind: 'fall_risk',
        title: `Morse Fall Scale: ${totalScore} — ${risk.label}`,
        data: { score: totalScore, risk: risk.label, answers },
      });
      setMsg('Fall risk assessment saved.');
    } catch (e) {
      setMsg(e.message || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {admissions.length > 0 && (
        <div className="row-actions mb">
          <label style={{ fontSize: 13, fontWeight: 600 }}>Patient:</label>
          <select className="input" style={{ maxWidth: 280 }} value={selectedAdm} onChange={(e) => { setSelectedAdm(e.target.value); setAnswers({}); }}>
            {admissions.map((a) => <option key={a.admissionId} value={a.admissionId}>{a.patientName} — Bed {a.bedLabel || a.bedId}</option>)}
          </select>
        </div>
      )}
      <div className="fall-risk-title">Morse Fall Scale Assessment</div>
      <div className="fall-questions">
        {FALL_QUESTIONS.map((q, i) => (
          <div key={i} className="fall-question">
            <div className="fall-q-text">{q.q}</div>
            <div className="fall-options">
              {q.opt.map((opt, j) => (
                <button
                  key={j}
                  type="button"
                  className="fall-opt-btn"
                  style={{
                    background: answers[`q${i}`] === j ? '#0d6e6a' : '#f1f5f9',
                    color: answers[`q${i}`] === j ? '#fff' : '#475569',
                  }}
                  onClick={() => setAnswers((a) => ({ ...a, [`q${i}`]: j }))}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="fall-score-box" style={{ background: risk.bg, borderColor: risk.color }}>
        <span className="fall-score-num" style={{ color: risk.color }}>{totalScore}</span>
        <span className="fall-risk-label" style={{ color: risk.color }}>{risk.label}</span>
        {totalScore >= 45 && <span style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>⚠ Implement fall prevention protocol immediately</span>}
      </div>
      <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
        <button type="button" className="btn btn-primary" disabled={busy || !selectedAdm} onClick={saveAssessment}>{busy ? 'Saving…' : 'Save assessment'}</button>
        {msg && <span className="hint" style={{ color: msg.includes('saved') ? '#10b981' : '#dc2626' }}>{msg}</span>}
      </div>
    </div>
  );
}

export default function NursingStation() {
  const { data, loading, error, reload } = useLiveData(() => ipApi.getAdmissions(50), []);
  const admissions = data?.admissions || [];
  const [view, setView] = useState('table');
  const [tab, setTab] = useState('overview');
  const [expandedId, setExpandedId] = useState(null);

  const criticalCount = admissions.filter((a) => {
    const v = a.vitals || {};
    const spo2 = parseFloat(v.spo2);
    const pulse = parseFloat(v.pulse);
    return (spo2 && spo2 < 95) || (pulse && (pulse < 50 || pulse > 120));
  }).length;

  const columns = [
    { key: 'patientName', label: 'Patient', render: (r) => (
      <div className="cell-stack">
        <span className="cell-strong">{r.patientName}</span>
        <span className="cell-sub">{r.ward} · Bed {r.bedLabel || r.bedId}</span>
      </div>
    )},
    { key: 'doctor', label: 'Doctor' },
    { key: 'vitals', label: 'Last Vitals', render: (r) => {
      const v = r.vitals;
      if (!v) return <span className="hint">No vitals</span>;
      const spo2 = parseFloat(v.spo2);
      const pulse = parseFloat(v.pulse);
      const isWarn = (spo2 && spo2 < 95) || (pulse && (pulse < 50 || pulse > 120));
      return (
        <span style={{ color: isWarn ? '#ef4444' : 'inherit', fontWeight: isWarn ? 700 : 400 }}>
          {isWarn && '⚠ '}BP {v.bp || '—'} · P {v.pulse || '—'} · SpO₂ {v.spo2 || '—'}
        </span>
      );
    }},
    { key: 'status', label: 'Status', render: (r) => <Badge tone="info">{r.status}</Badge> },
    { key: 'actions', label: 'Actions', render: (r) => (
      <div className="row-actions">
        <button type="button" className="btn btn-sm btn-secondary" onClick={() => setExpandedId(expandedId === r.admissionId ? null : r.admissionId)}>
          {expandedId === r.admissionId ? 'Hide' : 'Vitals'}
        </button>
        <Link to={`/ipd/patient/${r.admissionId}`} className="btn btn-sm btn-primary">Chart</Link>
      </div>
    )},
  ];

  return (
    <PageShell
      title="Nursing Station"
      subtitle="Patient care · MAR · Tasks · I/O Chart · Fall risk"
      actions={
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className={`btn btn-sm ${view === 'table' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView('table')} title="Table"><MdTableRows /></button>
          <button type="button" className={`btn btn-sm ${view === 'cards' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView('cards')} title="Cards"><MdGridView /></button>
        </div>
      }
    >
      <div className="stat-grid">
        <StatCard label="IP Patients" value={admissions.length} accent="#ec4899" icon={MdMonitorHeart} loading={loading} />
        <StatCard label="Critical / Watch" value={criticalCount} accent="#ef4444" icon={MdWarning} loading={loading} />
        <StatCard label="Outstanding" value={`₹${Number(data?.totalOutstanding || 0).toLocaleString('en-IN')}`} accent="#f97316" icon={MdAccountBalanceWallet} loading={loading} />
      </div>

      {error && <div className="error mb">{error}</div>}

      {/* ── Feature tabs ── */}
      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        {[
          { id: 'overview', label: '🏥 Patient Overview' },
          { id: 'mar', label: '💊 Medication (MAR)' },
          { id: 'tasks', label: '✅ Nurse Tasks' },
          { id: 'taskBoard', label: '📋 Task Board' },
          { id: 'io', label: '💧 I/O Chart' },
          { id: 'fallrisk', label: '⚠ Fall Risk' },
          { id: 'woundcare', label: '🩹 Wound Care' },
          { id: 'handover', label: '📋 Shift Handover' },
        ].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {tab === 'overview' && (
        view === 'cards' ? (
          <div className="ns-cards-grid">
            {admissions.map((adm) => {
              const v = adm.vitals || {};
              const spo2 = parseFloat(v.spo2);
              const isCritical = spo2 && spo2 < 95;
              return (
                <div key={adm.admissionId} className="ns-patient-card" style={{ borderLeft: `4px solid ${isCritical ? '#ef4444' : '#10b981'}` }}>
                  <div className="ns-card-head">
                    <div>
                      <div className="ns-card-name">{adm.patientName}</div>
                      <div className="ns-card-meta">{adm.ward} · Bed {adm.bedLabel || adm.bedId}</div>
                      <div className="ns-card-doctor">Dr. {adm.doctor}</div>
                    </div>
                    {isCritical && <span className="ns-critical-badge">CRITICAL</span>}
                  </div>
                  <VitalsPanel adm={adm} />
                  <div className="ns-card-foot">
                    <Link to={`/ipd/patient/${adm.admissionId}`} className="btn btn-sm btn-primary">Open Chart</Link>
                  </div>
                </div>
              );
            })}
            {admissions.length === 0 && !loading && <p style={{ color: '#94a3b8', gridColumn: '1/-1', padding: 24, textAlign: 'center' }}>No in-patients under care right now.</p>}
          </div>
        ) : (
          <>
            <DataTable columns={columns} rows={admissions} keyField="admissionId" loading={loading} emptyMessage="No in-patients under care right now." />
            {expandedId && (() => {
              const adm = admissions.find((a) => a.admissionId === expandedId);
              if (!adm) return null;
              return (
                <div className="card mb" style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
                  <div style={{ fontWeight: 700, marginBottom: 12 }}>Vitals — {adm.patientName} · {adm.ward} · Bed {adm.bedLabel || adm.bedId}</div>
                  <VitalsPanel adm={adm} />
                </div>
              );
            })()}
          </>
        )
      )}

      {tab === 'mar' && (
        <div className="card">
          <h3>Medication Administration Record (MAR)</h3>
          <p className="hint mb">Track every dose given. Click the time cell to mark as given.</p>
          <MarTab admissions={admissions} />
        </div>
      )}

      {tab === 'tasks' && (
        <div className="card">
          <h3>Nurse Task List</h3>
          <p className="hint mb">Daily duty checklist — add, complete, and prioritize tasks.</p>
          <TaskListTab />
        </div>
      )}

      {tab === 'io' && (
        <div className="card">
          <h3>Intake / Output (I/O) Chart</h3>
          <p className="hint mb">Track fluid balance for each patient. Enter values in mL.</p>
          <IOChartTab admissions={admissions} />
        </div>
      )}

      {tab === 'fallrisk' && (
        <div className="card">
          <h3>Fall Risk Assessment (Morse Scale)</h3>
          <p className="hint mb">Assess each patient's fall risk. Score ≥45 = High Risk.</p>
          <FallRiskTab admissions={admissions} />
        </div>
      )}

      {tab === 'woundcare' && <WoundCareTab admissions={admissions} />}
      {tab === 'handover' && <HandoverTab />}
      {tab === 'taskBoard' && (
        <div className="card">
          <h3>Nursing Task Board</h3>
          <p className="hint mb">Track patient-specific nursing tasks by status — Pending, Upcoming, and Done.</p>
          <NursingTaskBoard admissions={admissions} onReload={reload} />
        </div>
      )}
    </PageShell>
  );
}

/* ── Wound Care / Procedure Log Tab ── */
const WOUND_TYPES = ['Surgical Wound', 'Pressure Ulcer', 'Diabetic Ulcer', 'Burn', 'Abrasion', 'IV Site', 'Drain Site', 'Tracheostomy', 'Other'];
const DRESSING_TYPES = ['Dry Dressing', 'Wet Dressing', 'Hydrocolloid', 'Foam Dressing', 'Alginate', 'Transparent Film', 'Gauze + Betadine'];

function WoundCareTab({ admissions }) {
  const [records, setRecords] = useState({});
  const [form, setForm] = useState({ woundType: 'Surgical Wound', location: '', dressing: 'Dry Dressing', condition: 'Healing', notes: '', nextDue: '' });
  const [activePatient, setActivePatient] = useState(admissions[0]?.admissionId || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!activePatient) return;
    setBusy(true);
    try {
      const row = { ...form, at: new Date().toISOString(), id: Date.now() };
      await ipApi.addAdmissionActivity(activePatient, {
        kind: 'wound_care',
        title: `${form.woundType} — ${form.location || 'unspecified'}`,
        data: row,
      });
      setRecords((r) => ({
        ...r,
        [activePatient]: [...(r[activePatient] || []), row],
      }));
      setForm({ woundType: 'Surgical Wound', location: '', dressing: 'Dry Dressing', condition: 'Healing', notes: '', nextDue: '' });
    } finally {
      setBusy(false);
    }
  };

  const CONDITION_COLOR = { Healing: '#10b981', Stable: '#3b82f6', Worsening: '#ef4444', Infected: '#dc2626', 'Under Observation': '#f59e0b' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {admissions.map((a) => (
          <button key={a.admissionId} type="button" className={`emr-chip ${activePatient === a.admissionId ? 'active' : ''}`} onClick={() => setActivePatient(a.admissionId)}>
            {a.patientName}
          </button>
        ))}
        {admissions.length === 0 && <p className="hint">No in-patients.</p>}
      </div>

      {activePatient && (
        <>
          <div className="card mb" style={{ borderLeft: '4px solid #f97316' }}>
            <h3 style={{ marginTop: 0 }}>🩹 Log Wound Care / Dressing</h3>
            <div className="form-grid mb">
              <div className="field"><label>Wound Type</label>
                <select className="input" value={form.woundType} onChange={(e) => setForm({ ...form, woundType: e.target.value })}>
                  {WOUND_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="field"><label>Location on Body</label>
                <input className="input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Right lower leg, Sacrum" />
              </div>
              <div className="field"><label>Dressing Used</label>
                <select className="input" value={form.dressing} onChange={(e) => setForm({ ...form, dressing: e.target.value })}>
                  {DRESSING_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="field"><label>Wound Condition</label>
                <select className="input" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}>
                  {Object.keys(CONDITION_COLOR).map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="field"><label>Next Dressing Due</label>
                <input type="date" className="input" value={form.nextDue} onChange={(e) => setForm({ ...form, nextDue: e.target.value })} />
              </div>
              <div className="field full"><label>Notes / Observations</label>
                <textarea className="input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Exudate amount, odour, surrounding skin condition..." />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save Wound Care Record'}</button>
            </div>
          </div>

          <div>
            {(records[activePatient] || []).length === 0 && <p className="hint table-empty">No wound care records for this patient yet.</p>}
            {(records[activePatient] || []).map((r) => (
              <div key={r.id} className="card mb" style={{ borderLeft: `4px solid ${CONDITION_COLOR[r.condition] || '#e2e8f0'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 14 }}>{r.woundType} — {r.location || 'Location not specified'}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{new Date(r.at).toLocaleString('en-IN')} · Dressing: {r.dressing}</div>
                  </div>
                  <span style={{ fontSize: 11.5, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: `${CONDITION_COLOR[r.condition]}15`, color: CONDITION_COLOR[r.condition], border: `1px solid ${CONDITION_COLOR[r.condition]}40` }}>{r.condition}</span>
                </div>
                {r.notes && <div style={{ fontSize: 13, color: '#475569', fontStyle: 'italic' }}>{r.notes}</div>}
                {r.nextDue && <div style={{ fontSize: 12, color: '#f59e0b', fontWeight: 700, marginTop: 4 }}>📅 Next dressing: {r.nextDue}</div>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ── Shift Handover Tab ── */
const HANDOVER_SHIFTS = ['Morning (7am–2pm)', 'Afternoon (2pm–9pm)', 'Night (9pm–7am)'];

function HandoverTab() {
  const { records: handovers, create, loading } = useHmsRecords('nursing-handover');
  const [form, setForm] = useState({
    shift: 'Morning (7am–2pm)',
    handoverBy: '',
    handoverTo: '',
    generalNotes: '',
    criticalPatients: '',
    pendingTasks: '',
    equipmentStatus: '',
  });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await create({ ...form, at: new Date().toISOString() });
      setForm({ shift: 'Morning (7am–2pm)', handoverBy: '', handoverTo: '', generalNotes: '', criticalPatients: '', pendingTasks: '', equipmentStatus: '' });
    } finally {
      setBusy(false);
    }
  };

  const sorted = [...handovers].sort((a, b) => new Date(b.at || b.createdAt) - new Date(a.at || a.createdAt));

  return (
    <div>
      <form className="card mb" onSubmit={save}>
        <h3 style={{ marginTop: 0 }}>📋 New Shift Handover Report</h3>
        <div className="form-grid">
          <div className="field"><label>Shift</label>
            <select className="input" value={form.shift} onChange={(e) => setForm({ ...form, shift: e.target.value })}>
              {HANDOVER_SHIFTS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="field"><label>Handover From (Nurse)</label>
            <input className="input" required value={form.handoverBy} onChange={(e) => setForm({ ...form, handoverBy: e.target.value })} placeholder="Outgoing nurse name" />
          </div>
          <div className="field"><label>Handover To (Nurse)</label>
            <input className="input" required value={form.handoverTo} onChange={(e) => setForm({ ...form, handoverTo: e.target.value })} placeholder="Incoming nurse name" />
          </div>
          <div className="field full"><label>Critical Patients / Alerts</label>
            <textarea className="input" rows={2} value={form.criticalPatients} onChange={(e) => setForm({ ...form, criticalPatients: e.target.value })} placeholder="List any critical patients, changed conditions, new admissions..." />
          </div>
          <div className="field full"><label>Pending Tasks</label>
            <textarea className="input" rows={2} value={form.pendingTasks} onChange={(e) => setForm({ ...form, pendingTasks: e.target.value })} placeholder="IV pending, dressing due, lab sample pending..." />
          </div>
          <div className="field full"><label>General Notes</label>
            <textarea className="input" rows={2} value={form.generalNotes} onChange={(e) => setForm({ ...form, generalNotes: e.target.value })} placeholder="Ward status, equipment, any incidents..." />
          </div>
          <div className="field"><label>Equipment / Inventory Status</label>
            <input className="input" value={form.equipmentStatus} onChange={(e) => setForm({ ...form, equipmentStatus: e.target.value })} placeholder="e.g. Oxygen cylinder full, Glucometer battery low" />
          </div>
        </div>
        <div className="form-actions" style={{ marginTop: 12 }}>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : '✅ Submit Handover Report'}</button>
        </div>
      </form>

      {loading && <p className="hint mb">Loading handover reports…</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {sorted.map((h) => (
          <div key={h.recordId || h.id || h.at} className="card" style={{ borderLeft: '4px solid #8b5cf6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14 }}>{h.shift} Handover</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{new Date(h.at).toLocaleString('en-IN')} · {h.handoverBy} → {h.handoverTo}</div>
              </div>
            </div>
            {h.criticalPatients && <div style={{ marginBottom: 6 }}><strong style={{ fontSize: 12 }}>🚨 Critical:</strong> <span style={{ fontSize: 13, color: '#dc2626' }}>{h.criticalPatients}</span></div>}
            {h.pendingTasks && <div style={{ marginBottom: 6 }}><strong style={{ fontSize: 12 }}>📌 Pending:</strong> <span style={{ fontSize: 13 }}>{h.pendingTasks}</span></div>}
            {h.generalNotes && <div style={{ marginBottom: 6 }}><strong style={{ fontSize: 12 }}>📝 Notes:</strong> <span style={{ fontSize: 13 }}>{h.generalNotes}</span></div>}
            {h.equipmentStatus && <div><strong style={{ fontSize: 12 }}>🔧 Equipment:</strong> <span style={{ fontSize: 13, color: '#64748b' }}>{h.equipmentStatus}</span></div>}
          </div>
        ))}
        {sorted.length === 0 && !loading && <p className="hint table-empty">No handover reports yet. Fill the form above to create one.</p>}
      </div>
    </div>
  );
}

/* ═══ Nursing Task Board Component ═══ */
const TASK_TYPES_LIST = ['Vitals Check','Medication Administration','Wound Dressing','Blood Sample Collection','IV Line Change','Catheter Care','Patient Turning','Drain Measurement','Feeding (NGT)','Physiotherapy Assist','Patient Education','Discharge Preparation','Doctor Round Assist','ECG Recording'];

function flattenNursingTasks(admissions) {
  const doneRefs = new Set();
  (admissions || []).forEach((adm) => {
    (adm.activity || []).forEach((e) => {
      if (e.kind === 'nursing_task_done' && e.refEntryId) doneRefs.add(e.refEntryId);
    });
  });
  const tasks = [];
  (admissions || []).forEach((adm) => {
    (adm.activity || []).forEach((e) => {
      if (e.kind !== 'nursing_task') return;
      const isDone = doneRefs.has(e.entryId) || e.taskStatus === 'Done';
      tasks.push({
        id: e.entryId,
        admissionId: adm.admissionId,
        type: e.title,
        patient: adm.patientName,
        bed: e.bedLabel || adm.bedLabel || adm.bedId,
        time: e.scheduledTime || '',
        priority: e.priority || 'Medium',
        status: isDone ? 'Done' : (e.taskStatus || 'Pending'),
        doneBy: isDone ? (e.byName || '') : '',
        doneAt: isDone && e.at ? new Date(e.at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '',
      });
    });
  });
  return tasks.sort((a, b) => (a.time || '').localeCompare(b.time || ''));
}

function NursingTaskBoard({ admissions, onReload }) {
  const { user } = useAuth();
  const tasks = React.useMemo(() => flattenNursingTasks(admissions), [admissions]);
  const [staffName, setStaffName] = React.useState(user?.name || '');
  const [showAdd, setShowAdd] = React.useState(false);
  const [busy, setBusy] = React.useState('');
  const [err, setErr] = React.useState('');
  const [newTask, setNewTask] = React.useState({ type: 'Vitals Check', patient: '', bed: '', time: '', priority: 'Medium' });

  const markDone = async (task) => {
    setBusy(task.id);
    setErr('');
    try {
      await ipApi.addAdmissionActivity(task.admissionId, {
        kind: 'nursing_task_done',
        title: task.type,
        refEntryId: task.id,
        note: `Completed by ${staffName || user?.name || 'Nurse'}`,
      });
      if (onReload) await onReload();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy('');
    }
  };

  const addTask = async (e) => {
    e.preventDefault();
    const adm = admissions.find((a) => a.patientName === newTask.patient)
      || admissions.find((a) => a.patientName?.toLowerCase().includes(newTask.patient.toLowerCase()));
    if (!adm) {
      setErr('Select a patient from current IPD list.');
      return;
    }
    setBusy('new');
    setErr('');
    try {
      await ipApi.addAdmissionActivity(adm.admissionId, {
        kind: 'nursing_task',
        title: newTask.type,
        note: newTask.patient,
        bedLabel: newTask.bed || adm.bedLabel || adm.bedId,
        scheduledTime: newTask.time,
        priority: newTask.priority,
        taskStatus: 'Pending',
      });
      setNewTask({ type: 'Vitals Check', patient: '', bed: '', time: '', priority: 'Medium' });
      setShowAdd(false);
      if (onReload) await onReload();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy('');
    }
  };

  const pending = tasks.filter((t) => t.status === 'Pending');
  const upcoming = tasks.filter((t) => t.status === 'Upcoming');
  const done = tasks.filter((t) => t.status === 'Done');
  const prioColor = { High: '#dc2626', Medium: '#f59e0b', Low: '#10b981' };

  return (
    <div>
      {err && <div className="error mb">{err}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, padding: '8px 14px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Your Name:</label>
        <input className="input" style={{ maxWidth: 180 }} value={staffName} onChange={(e) => setStaffName(e.target.value)} placeholder="Nurse name" />
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowAdd((s) => !s)}>{showAdd ? 'Cancel' : '+ Add Task'}</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Pending', val: pending.length, color: '#f59e0b' }, { label: 'Upcoming', val: upcoming.length, color: '#6366f1' }, { label: 'Completed', val: done.length, color: '#10b981' }].map((s) => (
          <div key={s.label} className="card" style={{ textAlign: 'center', padding: 12, borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 24, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>{s.label}</div>
          </div>
        ))}
      </div>
      {showAdd && (
        <form className="card mb form-grid" onSubmit={addTask}>
          <div className="field"><label>Task Type</label><select className="input" value={newTask.type} onChange={(e) => setNewTask((t) => ({ ...t, type: e.target.value }))}>{TASK_TYPES_LIST.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div className="field"><label>Patient (IPD)</label>
            <select className="input" required value={newTask.patient} onChange={(e) => {
              const p = admissions.find((a) => a.patientName === e.target.value);
              setNewTask((t) => ({ ...t, patient: e.target.value, bed: p?.bedLabel || p?.bedId || t.bed }));
            }}>
              <option value="">— Select —</option>
              {admissions.map((a) => <option key={a.admissionId} value={a.patientName}>{a.patientName} · {a.bedLabel || a.bedId}</option>)}
            </select>
          </div>
          <div className="field"><label>Bed</label><input className="input" value={newTask.bed} onChange={(e) => setNewTask((t) => ({ ...t, bed: e.target.value }))} placeholder="B-101" /></div>
          <div className="field"><label>Time</label><input type="time" className="input" value={newTask.time} onChange={(e) => setNewTask((t) => ({ ...t, time: e.target.value }))} /></div>
          <div className="field"><label>Priority</label><select className="input" value={newTask.priority} onChange={(e) => setNewTask((t) => ({ ...t, priority: e.target.value }))}><option>High</option><option>Medium</option><option>Low</option></select></div>
          <div className="field full form-actions"><button type="submit" className="btn btn-primary" disabled={busy === 'new'}>{busy === 'new' ? 'Saving…' : '+ Add Task'}</button></div>
        </form>
      )}
      {pending.length > 0 && (
        <div className="card mb">
          <h3 style={{ marginTop: 0, color: '#f59e0b' }}>Pending Tasks ({pending.length})</h3>
          {pending.map((t) => (
            <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: prioColor[t.priority] || '#94a3b8', display: 'inline-block' }} />
                  <span style={{ fontWeight: 700 }}>{t.type}</span>
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{t.patient} · Bed {t.bed} · {t.time || '—'}</div>
              </div>
              <button type="button" className="btn btn-sm btn-primary" disabled={busy === t.id} onClick={() => markDone(t)}>{busy === t.id ? '…' : 'Done'}</button>
            </div>
          ))}
        </div>
      )}
      {done.length > 0 && (
        <div className="card" style={{ opacity: 0.85 }}>
          <h4 style={{ marginTop: 0, color: '#10b981' }}>Completed ({done.length})</h4>
          {done.map((t) => (
            <div key={t.id} style={{ fontSize: 12.5, padding: '6px 0', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 10, color: '#64748b' }}>
              <span style={{ color: '#10b981', fontWeight: 700 }}>✓</span>
              <span style={{ flex: 1 }}>{t.type} — {t.patient} (Bed {t.bed})</span>
              <span style={{ color: '#94a3b8' }}>{t.doneBy} · {t.doneAt}</span>
            </div>
          ))}
        </div>
      )}
      {pending.length === 0 && done.length === 0 && <p className="hint table-empty">No nursing tasks yet — add one for an admitted patient.</p>}
    </div>
  );
}
